import React, { useState, useEffect, memo } from 'react';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useImmer } from "use-immer";

import ResponsibleSelect from './ResponsibleSelect';
import MainForm from './MainForm';
import Slider from './Slider';

import InnerImageZoom from 'react-inner-image-zoom';
import 'react-inner-image-zoom/lib/styles.min.css';


import Alert from '@mui/material/Alert';

import { ExpensesData, Draft, Responsible } from './Types'
import { apiGetExpensesData, apiSetResponsible } from './Api';
import { addToCallChain } from './CallCenter';
import { clearTargetDraft } from './deepLink';
import { useApiData, LoadError, Loading } from './useApiData';
import useIsMobile from './useIsMobile';


export function getSortKey(draft: Draft): string {
    return draft.attachment.ImageDate + draft.draft_id
}

function compareDrafts(a: Draft, b: Draft) {
    if (a.draft_id == b.draft_id) return 0
    return getSortKey(a).localeCompare(getSortKey(b))
}

function Attachment({ draft }: { draft: Draft }) {
    const isMobile = useIsMobile()
    const attachment = draft.attachment
    // The zoomable image is a rendered thumbnail, so nothing in it can be selected. Link the
    // untouched file as well: a PDF opens in the browser's viewer, where text — an IBAN, a
    // bankgiro, a reference — can be copied straight into the form instead of retyped, and either
    // kind of file can be saved from there.
    const isPdf = attachment.ContentType === 'application/pdf'
    return (
        <div className="div-attachment">
            <div className="attachment-heading">
                <span>Uppladdad av {attachment.UploadedBy} {attachment.ImageDate}</span>
                {/* A draft is keyed by its attachment's id — the same id the attest posts to Visma. */}
                <a href={`/attachments/originals/${draft.draft_id}`} target="_blank" rel="noopener noreferrer">
                    Öppna originalet{isPdf ? ' (PDF)' : ''}
                </a>
            </div>
            {/* On a phone, zooming inside a container the width of the screen leaves nowhere to
                pan: the library intercepts touch, so the browser's own pinch is unavailable exactly
                where it is wanted. `fullscreenOnMobile` hands the whole screen over instead. The
                breakpoint matches the one in style.css that stacks the image above the form, so
                the two agree about what a phone is. */}
            {/* On a phone: a plain image, so the browser's own pinch and double-tap do the
                zooming. The library takes over touch events to offer one fixed magnification with
                drag-to-pan, and its way out is a tap that it disables by default — which is how a
                phone ended up with a zoom nobody could leave. Native gestures are what a phone
                user reaches for anyway, and the file behind this element is 1200-1600 px wide
                (a PDF page rendered at scale 2, or a photo's display copy, which keeps the photo's
                own resolution), so there are real pixels to zoom into: roughly 3-4x the width it is
                displayed at.

                The whole page zooms rather than the image alone, which on a phone is no loss —
                style.css stacks the image above the form there, so it is what fills the screen. */}
            {isMobile
                ? <img src={attachment.Url} alt="Underlaget"
                       style={{ width: '100%', height: 'auto' }}/>
                : <InnerImageZoom
                      src={attachment.Url}
                      moveType='drag'
                      hideCloseButton={true}
                  />}
        </div>
    )
}

// A role's own list: what it holds, and what nobody holds yet if it is one of the roles chosen to
// see new underlag (Inställningar → Företag). 'no_idea' is "nobody holds it, and this role has not
// set it aside".
function isInteresting(responsible: Responsible, receivesNew: boolean) {
    return responsible === 'myself' || (responsible === 'no_idea' && receivesNew)
}

function getVisibleDrafts(drafts: Draft[], responsibles: {[draft_id: string]: Responsible}, showAll: boolean,
                          receivesNew: boolean) {
    if (showAll) {
        return drafts
    }
    return drafts.filter(draft => isInteresting(responsibles[draft.draft_id], receivesNew))
}

function getIndex(drafts: Draft[], sortKey: string) {
    const index = drafts.findIndex(draft => getSortKey(draft) >= sortKey)
    return index === -1 ? drafts.length - 1 : index
}

function getSortedDrafts(drafts: Draft[]) {
    return drafts.toSorted((a, b) => getSortKey(a).localeCompare(getSortKey(b)))
}

function getDraftIdToResponsible(drafts: Draft[], responsibles: Responsible[]): {[draft_id: string]: Responsible} {
    let draftIdToResponsible: {[draft_id: string]: Responsible} = {}
    for (let i = 0; i < drafts.length; ++i) {
        draftIdToResponsible[drafts[i].draft_id] = responsibles[i]
    }
    return draftIdToResponsible
}

const Navigation = memo(({onUpdate, targetDraftId}: {onUpdate: () => void, targetDraftId?: string | null}) => {
    const { data, error, setData, reload } = useApiData(apiGetExpensesData, [])
    const [version, setVersion] = useState(0)

    function handleChangeData(newData: ExpensesData) {
        setData(newData)
        setVersion(version + 1)
    }

    if (error !== null) {
        return <LoadError onRetry={reload}/>
    }
    if (data === null) {
        return <Loading/>
    }

    return <InnerNavigation data={data} version={version} onChangeData={handleChangeData} onUpdate={onUpdate} targetDraftId={targetDraftId ?? null}/>
})

type DraftInfo = {
    draftId: string
    sortKey: string
}

function InnerNavigation({data, version, onChangeData, onUpdate, targetDraftId}: {
    data: ExpensesData,
    version: number,
    onChangeData: (newData: ExpensesData) => void,
    onUpdate: () => void,
    targetDraftId: string | null,
}) {
    const drafts = getSortedDrafts(data.drafts)
    const [responsibles, setResponsibles] = useImmer(getDraftIdToResponsible(data.drafts, data.draft_responsibles))
    // Rebuilt from every response the server sends back, not just the first. An attest that leaves
    // signatures outstanding hands the draft on to the next attestant server-side, and the attest
    // returns the refreshed state — without this the draft stayed "yours" until a reload. The local
    // copy exists only so a hand-off shows at once; the server's word replaces it whenever it comes.
    useEffect(() => {
        setResponsibles(getDraftIdToResponsible(data.drafts, data.draft_responsibles))
    }, [data, setResponsibles])
    const otherRoles = data.other_roles
    const [suppliers, setSuppliers] = useState(data.suppliers)
    const [reimbursees, setReimbursees] = useState(data.reimbursees)
    const costCenterItems = data.cost_center_items
    // A deep link opens straight on its draft; consume the target so later navigation is normal.
    // Clear the stash whether or not the draft is found: now that the list has loaded we've had our
    // one shot, so a stale/unknown id (e.g. an already-handled draft, or the leftover of a test
    // link) can't keep forcing Attestera on every reload.
    const [currentDraftInfo, setCurrentDraftInfo] = useState<DraftInfo | null>(() => {
        const target = targetDraftId !== null ? drafts.find(draft => draft.draft_id === targetDraftId) : undefined
        if (targetDraftId !== null) clearTargetDraft()
        if (target !== undefined) {
            return { draftId: target.draft_id, sortKey: getSortKey(target) }
        }
        return null
    })
    // A link whose draft is not here. Said rather than left as an ordinary list: the reader clicked
    // something specific and got a screen that looks like nothing happened. Worded for both causes,
    // since the company mismatch is caught elsewhere and names the company itself.
    const [linkMissed] = useState(
        () => targetDraftId !== null
              && !drafts.some(draft => draft.draft_id === targetDraftId))
//    const [currentSortKey, setCurrentSortKey] = useState<string | null>(null)
//    const [currentDraftId, setCurrentDraftId] = useState<string | null>(null)
    const [showAll, setShowAll] = useState(false)
    const [linkNoticeShown, setLinkNoticeShown] = useState(true)

    const onChangeShowAll = () => {
        setShowAll(!showAll);
    };

    // Set (and persist) who should look at a draft next — used by MainForm's one-click "Skicka
    // till <attestant>". Optimistic with revert, mirroring ResponsibleSelect.
    function commitResponsible(theDraftId: string, newResponsible: Responsible) {
        const previous = responsibles[theDraftId]
        setResponsibles((r) => { r[theDraftId] = newResponsible })
        addToCallChain(async () => {
            try {
                await apiSetResponsible({ draft_id: theDraftId, responsible: newResponsible })
            } catch {
                setResponsibles((r) => { r[theDraftId] = previous })
            }
        }, false)
    }
      
    function setCurrentIndex(index: number) {
        const draft = visibleDrafts[index]
        setCurrentDraftInfo({draftId: draft.draft_id, sortKey: getSortKey(draft)})
    }

    const visibleDrafts = getVisibleDrafts(drafts, responsibles, showAll, data.receives_new_underlag)

    const emptyList = visibleDrafts.length === 0

    let navigationButtons = <></>
    let draftDiv = <></>
    if (!emptyList) {
        let currentIndex: number
        let draftId: string
        let sortKey: string
        let currentDraft: Draft
        if (currentDraftInfo === null) {
            currentIndex = 0
            currentDraft = drafts[currentIndex]
            draftId = currentDraft.draft_id
            sortKey = getSortKey(currentDraft)
        } else {
            const draft = drafts.find(draft => draft.draft_id == currentDraftInfo.draftId)
            sortKey = draft !== undefined ? getSortKey(draft) : currentDraftInfo.sortKey
            currentIndex = getIndex(visibleDrafts, sortKey)
            currentDraft = visibleDrafts[currentIndex]
            draftId = currentDraft.draft_id

            function setResponsible(newResponsible: Responsible) {
                setResponsibles((responsibles) => { responsibles[draftId] = newResponsible })
            }

            draftDiv = <div id="div-recognize-draft">
                    <Slider item={currentDraft}
                            getComponentFromItem={draft => <Attachment draft={draft}/>}
                            compareItems={compareDrafts}/>
                    <div id="div-recognize-column">
                        {drafts.map((draft) =>
                            <MainForm
                                key={draft.draft_id + "_" + version}
                                show={draft.draft_id == currentDraftInfo.draftId}
                                currentDraft={draft}
                                reimbursees={reimbursees}
                                suppliers={suppliers}
                                costCenterItems={costCenterItems}
                                accounts={data.accounts}
                                fiscalYears={data.fiscal_years}
                                otherRoles={otherRoles}
                                onChangeSuppliers={setSuppliers}
                                onChangeReimbursees={setReimbursees}
                                onChangeData={(newData: ExpensesData) => { onChangeData(newData); onUpdate()}}
                                // Same data, new version: the key changes and the form remounts,
                                // re-reading every field from the server's copy.
                                onDiscardChanges={() => onChangeData(data)}
                                onSetResponsible={(responsible: Responsible) => commitResponsible(draft.draft_id, responsible)}
                                responsible={responsibles[draft.draft_id]}
                            />
                        )}
                        {/* Hand-off lives with the finish actions (just below the attest button): approve
                            it yourself, or choose who should look at it next. */}
                        <ResponsibleSelect
                            label="…eller låt någon annan titta på det härnäst"
                            value={responsibles[currentDraftInfo.draftId]}
                            currentDraftId={currentDraftInfo.draftId}
                            otherRoles={otherRoles}
                            onChangeResponsible={setResponsible}
                        />
                    </div>
                </div>
        }
        if (currentDraftInfo === null || currentDraftInfo.draftId != draftId || currentDraftInfo?.sortKey != sortKey) {
            setCurrentDraftInfo({draftId, sortKey})
        }

        function goToNextVisibleDraft() {        
            setCurrentIndex(currentIndex + 1)
        }
    
        function goToPreviousVisibleDraft() {
            setCurrentIndex(currentIndex - 1)
        }
        
        function canGoToPreviousVisibleDraft() {
            return currentIndex > 0
        }
    
        function canGoToNextVisibleDraft() {
            return currentIndex < visibleDrafts.length - 1
        }
        navigationButtons =
        <>
            <Button variant="outlined" disabled={!canGoToPreviousVisibleDraft()} id="button-previous" onClick={goToPreviousVisibleDraft}>Föregående</Button>
            <Button variant="outlined" disabled={!canGoToNextVisibleDraft()} id="button-next" onClick={goToNextVisibleDraft}>Nästa</Button>
            <Box sx={{ alignSelf: 'center', color: 'text.secondary' }}>{currentIndex + 1} av {visibleDrafts.length}</Box>
            </>
    }

    
    return (
        <Box sx={{ p: 2, display: 'flex', flexFlow: 'column nowrap', gap: 2 }}>
            <Box sx={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
                Att attestera{emptyList ? '' : ` (${visibleDrafts.length})`}
            </Box>
            {linkMissed && linkNoticeShown &&
                <Alert severity="info" onClose={() => setLinkNoticeShown(false)}>
                    Underlaget i länken finns inte i listan här. Det kan redan vara hanterat —
                    eller höra till en annan förening.
                </Alert>}
            {!emptyList && <Box sx={{ display: 'flex', flexFlow: 'row wrap', alignItems: 'center', gap: 1 }}>{ navigationButtons }</Box>}
            {/* A single subtle opt-in; the default (only mine) is what a first-timer wants. */}
            <FormControlLabel
                control={<Checkbox size="small" checked={showAll} onChange={onChangeShowAll}/>}
                label="Visa även underlag som någon annan ansvarar för"/>
            <div id="div-recognize-main">
                { emptyList &&
                <div id="div-recognize-empty-list">
                    Inga fler underlag att attestera.
                </div>}
                { draftDiv }
            </div>
        </Box>
    )
}

export default Navigation
